import { useTranslation } from 'react-i18next';
import { useContext, useState, useEffect } from 'react';
import { CrossFilled, DuoToneCourseFilled } from '@teo/components/icons';
import { axios } from '@/axios';
import { config } from '@/config';
import { AuthContext } from '@/components/Contexts/AuthContext';
import {
  IconButton,
  Tag,
  Button,
  InputSelect,
  TEOLoader,
  InputText,
} from '@teo/components';
import { langData } from '@/mocks/landData';
import { useNavigate } from 'react-router-dom';
import { courseTypeOptions } from '@/models/courseTypeOptions';
import { LangData } from '@/models/langData.interface';

export const CreateNewTemplatet = ({ setIsOpen }: any) => {
  const { t } = useTranslation();
  const { auth } = useContext(AuthContext);
  const authLang: any = auth?.user.lang;
  const authOrg: any = auth?.user?.activeOrganisationId;
  const [loading, setLoading] = useState(false);
  const [navigateTo, setNavigateTo] = useState<null | string>(null);
  const [courseType, setCourseType] = useState<string>('course');
  const [name, setName] = useState('');
  const [newLangData, setNewLangData] = useState<any>([]);
  const [selectLang, setSelectLang] = useState<any>(null);

  useEffect(() => {
    const sortLangData = langData
      ?.map((el) => ({
        ...el,
        name: t(`langs.${el?.value}`),
      }))
      .sort((a, b) => a.name.localeCompare(b.name));
    setSelectLang(
      sortLangData?.find((option: LangData) => option?.value === authLang)
    );
    setNewLangData(sortLangData);
  }, [langData]);

  const navigate = useNavigate();

  useEffect(() => {
    if (navigateTo) {
      navigate(navigateTo);
    }
  }, [navigateTo]);

  const handleStart = async () => {
    setLoading(true);
    const title = name
      ? name
      : t('pages.create_publication.title_new_template');
    const newTemplate = await axios.post(`${config.backend}/coursecontents`, {
      name: title,
      lang: selectLang?.value ? selectLang?.value : authLang,
      template: 'template',
    });
    const publicationId = newTemplate?.data?.id;

    await Promise.allSettled([
      axios.post(`${config.backend}/membercoursecontents`, {
        role: 90,
        coursecontentId: publicationId,
        organisationId: authOrg,
      }),
      axios.put(`${config.backend}/coursecontent/${publicationId}`, {
        authorId: auth?.user?.id,
        publicationStatus: 'concept-template',
        publicationCourseType: courseType,
      }),
      axios.post(`${config.backend}/directories`, {
        coursecontentId: parseInt(publicationId, 10),
        name: 'root',
      }),
    ]).then(() => {
      setNavigateTo(`/${authLang}/create/${authOrg}/courses/${publicationId}`);
    });
  };

  return (
    <div className="fixed top-0 left-0 z-[100] flex h-screen w-screen flex-col overflow-y-auto bg-white">
      <div className="flex min-h-16 flex-col justify-start border-b border-grey-02 py-2 pr-2 sm:flex-row sm:items-center">
        <div
          data-testid="clouseBtn"
          className="flex min-h-16 items-center justify-start"
        >
          <IconButton
            className="my-auto mx-3 sm:mx-6"
            Icon={CrossFilled}
            variant="ghost"
            onClick={() => {
              setIsOpen(false);
            }}
          />
          <div className="flex items-center">
            <div className="mr-8">
              <DuoToneCourseFilled className="w-6" />
            </div>
            <p className="mr-8 font-medium">
              {t(`pages.create_publication.default_template_title`)}
            </p>
            <Tag title={t('pages.create_publication.draft')} size="md" />
          </div>
        </div>
      </div>
      <div className="m-auto flex w-full max-w-3xl flex-col gap-8 p-3 py-5 md:min-w-[650px] md:gap-6">
        {loading ? (
          <div className="flex justify-center">
            <TEOLoader
              loaderSrc="/images/TEOloader.gif"
              className="w-[110px]"
            />
          </div>
        ) : (
          <>
            <div className="">
              <h3 className="mb-4 text-xl font-normal">
                {t(`pages.create_publication.give_title_template`)}
              </h3>

              <InputText
                value={name}
                onChange={(ev: any) => setName(ev.target.value)}
                placeholder={t(
                  `pages.create_publication.title_template_placeholder`
                )}
              />
            </div>
            <div>
              <h3 className="mb-2 text-xl font-normal">
                {t(`pages.create_publication.template_course_type`)}
              </h3>
              <div id="courseType">
                <InputSelect
                  onChange={(courseType) => {
                    setCourseType(courseType.value);
                  }}
                  value={courseTypeOptions.find((x) => x.value === courseType)}
                  options={courseTypeOptions}
                >
                  {(options) => (
                    <span>{t(`courseTypes.${options?.name}`)}</span>
                  )}
                </InputSelect>
              </div>
            </div>
            <div className="">
              <h3 className="mb-2 text-xl font-normal">
                {t(`pages.create_publication.template_lang`)}
              </h3>
              <div id="templateLang">
                <InputSelect<LangData>
                  onChange={(lang) => {
                    setSelectLang(lang);
                  }}
                  value={
                    selectLang
                      ? selectLang
                      : newLangData?.find(
                          (option: LangData) => option?.value === authLang
                        )
                  }
                  options={newLangData}
                >
                  {(options) => <span>{options?.name} </span>}
                </InputSelect>
              </div>
            </div>
            <div className="flex">
              <Button type="button" onClick={handleStart} className="ml-auto">
                {t('pages.create_publication.btn_start_editing')}
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};
